import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Package2, FileText, Settings, LayoutDashboard, Menu, LogOut, Palmtree, ChevronLeft, ChevronRight, UserCog, User } from "lucide-react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUserRole } from "@/lib/useUserRole";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function Sidebar({ open, setOpen }: SidebarProps) {
  const { signOut } = useAuth();
  const { role } = useUserRole();
  
  // Check if user can access admin features
  const isAdmin = role === 'admin';

  // Fetch settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from("settings")
          .select("key, value");

        if (error) {
          console.error("Error fetching settings:", error);
          return {
            site_name: "Easy Bali Dashboard",
          };
        }

        // Transform array of settings to object
        return data.reduce((acc, curr) => {
          acc[curr.key] = curr.value;
          return acc;
        }, {} as Record<string, string>);
      } catch (error) {
        console.error("Error in settings query:", error);
        return {
          site_name: "Easy Bali Dashboard",
        };
      }
    },
  });

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(!open)}
        className="fixed top-4 left-4 z-50 lg:hidden"
      >
        <Menu className="h-6 w-6" />
      </Button>

      {/* Backdrop for mobile */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <nav
        className={cn(
          "fixed left-0 top-0 z-50 h-full border-r bg-background transition-all duration-300 lg:translate-x-0",
          open ? "w-64" : "w-16",
          !open && "lg:w-16",
          !open && "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className={cn("flex h-16 items-center border-b px-4", open && "px-6")}>
          <NavLink to="/" className="flex items-center gap-2 font-semibold">
            <Package2 className="h-6 w-6" />
            {open && <span>{settings?.site_name || "Easy Bali Dashboard"}</span>}
          </NavLink>
        </div>

        <ScrollArea className="flex flex-col h-[calc(100vh-4rem)]">
          <div className="flex-1 p-4">
            <div className="space-y-2">
              <NavLink to="/" end>
                {({ isActive }) => (
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    className={cn("w-full", open ? "justify-start" : "justify-center")}
                  >
                    <LayoutDashboard className={cn("h-4 w-4", open && "mr-2")} />
                    {open && <span>Dashboard</span>}
                  </Button>
                )}
              </NavLink>

              {/*
              {isAdmin && (
                <NavLink to="/users">
                  {({ isActive }) => (
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      className={cn("w-full", open ? "justify-start" : "justify-center")}
                    >
                      <User className={cn("h-4 w-4", open && "mr-2")} />
                      {open && <span>Users</span>}
                    </Button>
                  )}
                </NavLink>
              )}*/}

              {isAdmin && (
                <NavLink to="/tours">
                  {({ isActive }) => (
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      className={cn("w-full", open ? "justify-start" : "justify-center")}
                    >
                      <Palmtree className={cn("h-4 w-4", open && "mr-2")} />
                      {open && <span>Tours</span>}
                    </Button>
                  )}
                </NavLink>
              )}

              {isAdmin && (
                <NavLink to="/tourpackage">
                  {({ isActive }) => (
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      className={cn("w-full", open ? "justify-start" : "justify-center")}
                    >
                      <Package2 className={cn("h-4 w-4", open && "mr-2")} />
                      {open && <span>Tour Packages</span>}
                    </Button>
                  )}
                </NavLink>
              )}

              <NavLink to="/inquiry">
                {({ isActive }) => (
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    className={cn("w-full", open ? "justify-start" : "justify-center")}
                  >
                    <FileText className={cn("h-4 w-4", open && "mr-2")} />
                    {open && <span>Inquiry</span>}
                  </Button>
                )}
              </NavLink>
            </div>

            <div className="pt-4 mt-4 border-t">
              {isAdmin && (
                <NavLink to="/settings">
                  {({ isActive }) => (
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      className={cn("w-full", open ? "justify-start" : "justify-center")}
                    >
                      <Settings className={cn("h-4 w-4", open && "mr-2")} />
                      {open && <span>Settings</span>}
                    </Button>
                  )}
                </NavLink>
              )}
              
              {isAdmin && (
                <NavLink to="/admin-users">
                  {({ isActive }) => (
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      className={cn("w-full", open ? "justify-start" : "justify-center")}
                    >
                      <UserCog className={cn("h-4 w-4", open && "mr-2")} />
                      {open && <span>Admin Users</span>}
                    </Button>
                  )}
                </NavLink>
              )}
            </div>
          </div>

          <div className="p-4 border-t mt-auto">
            <Button
              variant="ghost"
              className={cn(
                "w-full mb-4 text-red-500 hover:text-red-500 hover:bg-red-50",
                open ? "justify-start" : "justify-center"
              )}
              onClick={() => signOut()}
            >
              <LogOut className={cn("h-4 w-4", open && "mr-2")} />
              {open && <span>Sign Out</span>}
            </Button>
            
            {/* Collapse button */}
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-center"
              onClick={() => setOpen(!open)}
            >
              {open ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {open && <span className="ml-2">Collapse</span>}
            </Button>
          </div>
        </ScrollArea>
      </nav>
    </>
  );
}
